
import { useTdsCommon } from '../../hooks/useTdsCommon'
import { getStateCode, formatDate, formatDecimalAmount, padString, padNumber, reverseStateCodes } from '../../utils/tdsCommon'
import { extractProfileDataApi } from '../../utils/tdsFileHandlers'
import { getStorageItem, setStorageItem } from '../../utils/storage'
import React, { useState, useEffect, useCallback, useRef } from 'react'

import { motion } from 'framer-motion'
import { FileText, User, Upload, ArrowLeft, Building2, X, Trash2, CheckCircle, Download, Eye, EyeOff } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import axios from 'axios'

import TdsDetailsTable from '../../components/dashboard/TdsDetailsTable'
import ChallanDetailsTable from '../../components/dashboard/ChallanDetailsTable'
import SalaryDetailsTable from '../../components/dashboard/SalaryDetailsTable'
import SeniorCitizens194PTable from '../../components/dashboard/SeniorCitizens194PTable'
import TdsSidebar from '../../components/dashboard/TdsSidebar'
import TdsProfileSection from '../../components/dashboard/TdsProfileSection'
import { useError } from '../../contexts/ErrorContext'


const stateOptions = [
    'ANDAMAN AND NICOBAR ISLANDS',
    'ANDHRA PRADESH',
    'ARUNACHAL PRADESH',
    'ASSAM',
    'BIHAR',
    'CHANDIGARH',
    'CHHATTISGARH',
    'DADRA & NAGAR HAVELI AND DAMAN & DIU',
    'DELHI',
    'GOA',
    'GUJARAT',
    'HARYANA',
    'HIMACHAL PRADESH',
    'JAMMU & KASHMIR',
    'JHARKHAND',
    'KARNATAKA',
    'KERALA',
    'LADAKH',
    'LAKSHWADEEP',
    'MADHYA PRADESH',
    'MAHARASHTRA',
    'MANIPUR',
    'MEGHALAYA',
    'MIZORAM',
    'NAGALAND',
    'ODISHA',
    'PONDICHERRY',
    'PUNJAB',
    'RAJASTHAN',
    'SIKKIM',
    'TAMIL NADU',
    'TELANGANA',
    'TRIPURA',
    'UTTAR PRADESH',
    'UTTARAKHAND',
    'WEST BENGAL'
];

const sectionOptions24Q_old = [
    { value: '92A', label: '92A - 192 (Payment to Govt. Employees other than Union Govt.)' },
    { value: '92B', label: '92B - 192 (Payment to Employees other than Govt. Employees)' },
    { value: '92C', label: '92C - 192 (Payment to Union Govt. Employees)' },
    { value: '94P', label: '94P - 194P (Specified Senior Citizens)' }
];

const sectionOptions24Q_new = [
    { value: '1001', label: '1001 - Salary – State Government Employees' },
    { value: '1002', label: '1002 - Salary – Non-Government/Private Sector Employees' },
    { value: '1003', label: '1003 - Salary – Union/Central Government Employees' },
    { value: '1032', label: '1032 - Specified Senior Citizen Pension & Interest' }
];

const Tds24Q4Form = ({ isMobile, onSidebarStateChange, onQuarterChange }) => {
    const csiFileInputRef = useRef(null);
    console.log('Rendering Tds24Q4Form component');
    const navigate = useNavigate()
    const { addError } = useError()
    const [activeSection, setActiveSection] = useState('profile')
    const [salaryData, setSalaryData] = useState([]);
    const [originalSalaryData, setOriginalSalaryData] = useState([]);
    const [seniorCitizenData, setSeniorCitizenData] = useState([]);
    const [originalSeniorCitizenData, setOriginalSeniorCitizenData] = useState([]);
    const quarterOptions = ['Q1', 'Q2', 'Q3', 'Q4'];
    const formOptions = ['24Q', '26Q', '27Q', '27EQ'];
    const pendingFileContentRef = useRef(null);

    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [sidebarState, setSidebarState] = useState({
        isExpanded: true,
        isMobile: false
    });



    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const currentFyStart = currentMonth >= 3 ? currentYear : currentYear - 1;

    const financialYearOptions = [];
    for (let start = currentFyStart; start >= 2023; start--) {
        financialYearOptions.push(`${start}-${(start + 1).toString().slice(-2)}`);
    }


    const handleClearData = () => {
        if (window.confirm('Are you sure you want to clear all data? This action cannot be undone.')) {
            const initialProfile = {
                section1: { taxDeduction: '', permanentAccountNumber: '', typeOfDeduction: '', receiptNoOriginal: '', receiptNoEarlier: '', isEarlierReturnFiled: 'No' },
                section2: { name: '', branchDivision: '', flatNo: '', nameOfPremises: '', roadStreet: '', areaLocality: '', townCity: '', pinCode: '', state: '', email: '', stdCode: '', telephoneNo: '' },
                section3: { sameAsAbove: false, name: '', designation: '', permanentAccountNumber: '', nameOfPremises: '', flatNo: '', areaLocality: '', roadStreet: '', pinCode: '', townCity: '', telephone: '', telephoneAlternate: '', state: '', email: '', emailAlternate: '', mobileNo: '', hasAddressChanged: '', hasRegularStatement: '' }
            };
            if (typeof setChallanData === 'function') setChallanData([]);
            if (typeof setTdsData === 'function') setTdsData([]);
            if (typeof setProfileData === 'function') setProfileData(initialProfile);
            localStorage.removeItem('tds24q4ChallanData');
            localStorage.removeItem('tds24q4TdsData');
            localStorage.removeItem('tds24q4OriginalTdsData');
            localStorage.removeItem('tds24q4ProfileData');
        }
    };


    const handleSidebarStateChange = useCallback((state) => {
        setSidebarState(state);
    }, []);

    const {
        profileData,
        challanData,
        tdsData,
        originalTdsData,
        csiFile,
        consoFileContent,
        isValidating,
        showCsiUploadPopup,
        showDownloadPrompt,
        selectedFinancialYear,
        selectedQuarter,
        selectedForm,
        selectedReturnType,
        challanDateRange,
        setProfileData,
        setChallanData,
        setTdsData,
        setOriginalTdsData,
        setCsiFile,
        setConsoFileContent,
        setIsValidating,
        setShowCsiUploadPopup,
        setShowDownloadPrompt,
        setSelectedFinancialYear,
        setSelectedQuarter,
        setSelectedForm,
        setSelectedReturnType,
        handlePrepareReturnFile,
        handleValidateReturn,
        handleSkipDownload,
        handleCancelValidation,
        executeValidation
    } = useTdsCommon({
        formName: 'tds24Q',
        defaultFormType: '24Q',
        isCorrection: false,
        generateTdsStatementFile,
        validateDataForReturn,
        onValidationError: (errors) => {
            addError('Please fix the following errors before preparing return:')
            errors.forEach(err => addError(`• ${err}`))
            setSidebarOpen(true)
            setActiveSection('errors')
        }
    });

    const [isProfileEditing, setIsProfileEditing] = useState(true)
    const [downloadPassword, setDownloadPassword] = useState('')
    const [showDownloadPassword, setShowDownloadPassword] = useState(false)
    const [isDownloadingCsi, setIsDownloadingCsi] = useState(false)
    const handleCsiDownload = async () => {
        if (!downloadPassword) {
            addError('Password is required');
            return;
        }

        setIsDownloadingCsi(true);
        try {
            const response = await axios.post('/api/login-income-tax', {
                credentials: {
                    tan: profileData?.section1?.taxDeduction,
                    password: downloadPassword
                },
                dateRange: challanDateRange
            });

            if (response.data.success && response.data.csiContent) {
                toast.success('CSI File Downloaded Successfully!');
                setShowDownloadPrompt(false);
                setDownloadPassword('');

                const binaryString = window.atob(response.data.csiContent.replace(/\s/g, ''));
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const file = new File([bytes], response.data.fileName || 'downloaded.csi', { type: 'text/plain' });
                if (typeof setCsiFile === 'function') setCsiFile(file);
                if (typeof executeValidation === 'function') executeValidation(file);
            } else {
                const errMsg = response.data.error || 'Failed to download CSI file. Please check password.';
                toast.error(errMsg);
            }
        } catch (err) {
            console.error(err);
            addError('Failed to initiate download');
        } finally {
            setIsDownloadingCsi(false);
        }
    };


    // Profile Pre-fill Automation State
    const [showPrefillPrompt, setShowPrefillPrompt] = useState(false);
    const [prefillPassword, setPrefillPassword] = useState('');
    const [showPrefillPassword, setShowPrefillPassword] = useState(false);
    const [isExtractingProfile, setIsExtractingProfile] = useState(false)

    const handleProfilePrefill = async () => {
        if (!prefillPassword) {
            toast.error('Portal password is required');
            return;
        }

        setIsExtractingProfile(true);
        try {
            const { success, data, error } = await extractProfileDataApi({
                tan: profileData?.section1?.taxDeduction,
                password: prefillPassword
            });

            if (success && data) {
                const newData = { ...profileData };
                if (data.pan) {
                    if (newData.section1) newData.section1.permanentAccountNumber = data.pan;
                    if (newData.section3) newData.section3.permanentAccountNumber = data.pan;
                }
                if (data.name) {
                    if (newData.section2) newData.section2.name = data.name;
                }
                if (data.keyPerson) {
                    if (data.keyPerson.name && newData.section3) newData.section3.name = data.keyPerson.name;
                    if (data.keyPerson.pan && newData.section3) newData.section3.permanentAccountNumber = data.keyPerson.pan;
                }
                if (data.email) {
                    if (newData.section2) newData.section2.email = data.email;
                    if (newData.section3) newData.section3.email = data.email;
                }
                if (data.mobile) {
                    if (newData.section2) newData.section2.mobileNo = data.mobile;
                    if (newData.section3) newData.section3.mobileNo = data.mobile;
                }
                if (data.address) {
                    if (newData.section2) {
                        newData.section2.flatNo = data.address.flatNo || '';
                        newData.section2.nameOfPremises = data.address.premisesName || '';
                        newData.section2.roadStreet = data.address.roadStreet || '';
                        newData.section2.areaLocality = data.address.areaLocality || '';
                        newData.section2.townCity = data.address.city || '';
                        newData.section2.pinCode = data.address.pinCode || '';
                        if (data.address.state) {
                            newData.section2.state = data.address.state.toUpperCase().trim();
                        }
                    }
                }

                if (typeof setProfileData === 'function') {
                    setProfileData(newData);
                }
                toast.success('Profile Data Auto-Filled Successfully!');
                setShowPrefillPrompt(false);
                setPrefillPassword('');
            } else {
                toast.error(error || 'Failed to extract profile data');
            }
        } catch (err) {
            console.error(err);
            toast.error('An error occurred during extraction');
        } finally {
            setIsExtractingProfile(false);
        }
    };
    ;

    const [originalProfileData, setOriginalProfileData] = useState(null)
    const [originalChallanData, setOriginalChallanData] = useState([])
    const [batchNumber, setBatchNumber] = useState('1')
    const [consoFhHash, setConsoFhHash] = useState('')

    useEffect(() => {
        (async () => {
            const savedOriginalChallanData = await getStorageItem('tds24QOriginalChallanData')
            const savedOriginalProfileData = await getStorageItem('tds24QOriginalProfileData')
            const savedSalaryData = await getStorageItem('tds24QSalaryData')
            const savedOriginalSalaryData = await getStorageItem('tds24QOriginalSalaryData')
            const savedSeniorCitizenData = await getStorageItem('tds24QSeniorCitizenData')
            const savedOriginalSeniorCitizenData = await getStorageItem('tds24QOriginalSeniorCitizenData')
            const savedBatchNumber = localStorage.getItem('tds24QBatchNumber')

            if (savedOriginalChallanData) setOriginalChallanData(savedOriginalChallanData)
            if (savedOriginalProfileData) setOriginalProfileData(savedOriginalProfileData)
            if (savedSalaryData && savedSalaryData.length > 0) setSalaryData(savedSalaryData)
            if (savedOriginalSalaryData && savedOriginalSalaryData.length > 0) setOriginalSalaryData(savedOriginalSalaryData)
            if (savedSeniorCitizenData && savedSeniorCitizenData.length > 0) setSeniorCitizenData(savedSeniorCitizenData)
            if (savedOriginalSeniorCitizenData && savedOriginalSeniorCitizenData.length > 0) setOriginalSeniorCitizenData(savedOriginalSeniorCitizenData)
            if (savedBatchNumber) setBatchNumber(savedBatchNumber)
        })()
    }, [])

    const handleChallanDataChange = setChallanData;
    const handleTdsDataChange = setTdsData;


    const handleSalaryDataChange = (data) => {
        setSalaryData(data)
        setStorageItem('tds24QSalaryData', data)
    }

    const handleSeniorCitizenDataChange = (data) => {
        setSeniorCitizenData(data)
        setStorageItem('tds24QSeniorCitizenData', data)
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Conso File Parsing Logic (Annexure I & II: CD, DD, SD, S16, C6A, 94P)
    // ─────────────────────────────────────────────────────────────────────────
    const parseConsoFile = (fileContent) => {
        if (!fileContent) return;
        const lines = fileContent.split('\n');

        // Validate BH record form type
        const bhLine = lines.find(l => {
            const p = l.split('^');
            return p[1] === 'BH';
        });

        if (bhLine) {
            const bhParts = bhLine.split('^');
            const formInConso = (bhParts[4] || '').toUpperCase().trim();
            if (formInConso && formInConso !== '24Q' && formInConso !== '138') {
                toast.error(`Invalid Conso File! Expected Form 24Q, but file contains Form ${formInConso}.`);
                alert(`Invalid Conso File!\n\nThis form is Form 24Q (Q4), but the uploaded Conso file belongs to Form ${formInConso}.\nPlease upload the correct Form 24Q Conso file.`);
                return;
            }
        }

        setConsoFileContent(fileContent);

        const newChallans = [];
        const newTdsData = [];
        const newSalaryData = [];
        const newSeniorCitizenData = [];
        const salaryRecordMap = {};
        let currentChallan = null;
        let currentSalary = null;
        let rowIndex = 0;

        const getMappedState = (code) => {
            if (!code) return '';
            const padded = code.padStart(2, '0');
            return reverseStateCodes[padded] || reverseStateCodes[code] || '';
        };

        const parseDateDDMMYYYY = (val) => {
            if (!val || val.length !== 8) return '';
            const day = val.slice(0, 2);
            const month = val.slice(2, 4);
            const year = val.slice(4, 8);
            return `${year}-${month}-${day}`;
        };

        try {
            lines.forEach((line) => {
                if (!line.trim()) return;
                const parts = line.split('^');
                const recordType = parts[1];

                if (recordType === 'FH') {
                    setConsoFhHash(parts[14] || parts[17] || '');
                } else if (recordType === 'BH') {
                    const bhData = {
                        section1: {
                            taxDeduction: parts[12] || '', // TAN (Field 13)
                            permanentAccountNumber: parts[14] || '', // PAN (Field 15)
                            typeOfDeduction: parts[31] || '', // Type/Status (Field 32)
                            receiptNoOriginal: parts[7] || '', // Token No (Field 8)
                            receiptNoEarlier: parts[8] || '', // Prev Token No (Field 9)
                            isEarlierReturnFiled: parts[51] === 'Y' ? 'Yes' : 'No'
                        },
                        section2: {
                            name: parts[18] || '', // Field 19
                            branchDivision: parts[19] || '', // Field 20
                            flatNo: parts[20] || '', // Field 21
                            nameOfPremises: parts[21] || '', // Field 22
                            roadStreet: parts[22] || '', // Field 23
                            areaLocality: parts[23] || '', // Field 24
                            townCity: parts[24] || '', // Field 25
                            state: getMappedState(parts[25]), // Field 26
                            pinCode: parts[26] || '', // Field 27
                            email: parts[27] || '', // Field 28
                            stdCode: parts[28] || '', // Field 29
                            telephoneNo: parts[29] || '', // Field 30
                            gstin: parts[68] || ''
                        },
                        section3: {
                            name: parts[32] || '', // Field 33
                            designation: parts[33] || '', // Field 34
                            flatNo: parts[34] || '', // Field 35
                            nameOfPremises: parts[35] || '', // Field 36
                            roadStreet: parts[36] || '', // Field 37
                            areaLocality: parts[37] || '', // Field 38
                            townCity: parts[38] || '', // Field 39
                            state: getMappedState(parts[39]), // Field 40
                            pinCode: parts[40] || '', // Field 41
                            email: parts[41] || '', // Field 42
                            mobileNo: parts[47] || '', // Field 48
                            stdCode: parts[43] || '', // Field 44
                            telephone: parts[44] || '', // Field 45
                            telephoneAlternate: parts[45] || '', // Field 46
                            permanentAccountNumber: parts[58] || '', // Field 59
                            hasAddressChanged: parts[46] === 'Y' ? 'Yes' : 'No',
                            sameAsAbove: false
                        }
                    };
                    setProfileData(bhData);
                    setOriginalProfileData(JSON.parse(JSON.stringify(bhData)));
                    setStorageItem('tds24QOriginalProfileData', bhData);
                    setBatchNumber(parts[2] || '1');
                    localStorage.setItem('tds24QBatchNumber', parts[2] || '1');

                    // Sync Financial Year if present in Conso BH
                    if (parts[16] && parts[16].length === 6) {
                        const start = parts[16].slice(0, 4);
                        const end = parts[16].slice(4, 6);
                        const fy = `${start}-${end}`;
                        if (typeof setSelectedFinancialYear === 'function') setSelectedFinancialYear(fy);
                    }
                    if (parts[17]) {
                        if (typeof setSelectedQuarter === 'function') setSelectedQuarter(parts[17]);
                    }
                } else if (recordType === 'CD') {
                    const challan = {
                        id: Date.now() + rowIndex++,
                        challanSerialNo: parts[11] || '',
                        originalChallanNo: parts[11] || '',
                        bsrCode: parts[15] || '',
                        originalBsrCode: parts[15] || '',
                        dateOfDeposit: parseDateDDMMYYYY(parts[17]),
                        originalDateOfDeposit: parts[17] || '',
                        minorHead: parts[40] || '',
                        sectionCode: '',
                        tdsAmount: parseFloat(parts[21] || 0),
                        surcharge: parseFloat(parts[22] || 0),
                        educationCess: parseFloat(parts[23] || 0),
                        interest: parseFloat(parts[24] || 0),
                        other: parseFloat(parts[25] || 0),
                        totalAmount: parseFloat(parts[26] || 0),
                        originalTotalAmount: parseFloat(parts[26] || 0),
                        feesAmount: parseFloat(parts[38] || 0),
                        bookEntry: parts[36] === 'Y',
                        recordNo: parts[3] ? parseInt(parts[3], 10) : '',
                        source: 'conso',
                        higherEducationCess: 0
                    };
                    currentChallan = challan;
                    newChallans.push(challan);
                } else if (recordType === 'DD') {
                    if (!currentChallan) return;
                    const fullName = (parts[12] || '').trim();
                    const origTds = parseFloat(parts[13] || 0);
                    const origSurch = parseFloat(parts[14] || 0);
                    const origCess = parseFloat(parts[15] || 0);
                    const computedTotalTax = origTds + origSurch + origCess;
                    const origTotalTaxFromField = (parts[16] !== undefined && parts[16] !== '') ? parseFloat(parts[16]) : computedTotalTax;
                    const origTotalTaxDepositedFromField = (parts[18] !== undefined && parts[18] !== '') ? parseFloat(parts[18]) : origTotalTaxFromField;

                    const deductee = {
                        id: Date.now() + rowIndex++,
                        challanId: currentChallan.id,
                        deducteeCode: parts[7] === '1' ? '01' : '02',
                        panOfDeductee: parts[9] || '',
                        originalPan: parts[9] || '',
                        firstName: fullName,
                        middleName: '',
                        lastName: '',
                        nameOfDeductee: parts[12] || '',
                        amountOfPayment: parseFloat(parts[21] || 0),
                        dateOfPayment: parseDateDDMMYYYY(parts[22]),
                        dateOfDeduction: parseDateDDMMYYYY(parts[23]),
                        rateOfTax: parseFloat(parts[25] || 0).toFixed(4),
                        tdsAmount: origTds,
                        surcharge: origSurch,
                        cess: origCess,
                        sectionCode: parts[32] || parts[33] || '',
                        reasonForNonDeduction: parts[29] || '',
                        certificateNo197: parts[34] || '',
                        source: 'conso',
                        recordNo: parts[4] || '',
                        challanRecordNo: parts[3] || '',
                        consoMode: parts[5] || '',
                        originalTotalTax: origTotalTaxFromField,
                        originalTotalTaxDeposited: origTotalTaxDepositedFromField,
                        originalTdsAmount: origTds,
                        originalSurcharge: origSurch,
                        originalCess: origCess,
                        originalRefNo: parts[11] || parts[10] || ''
                    };
                    newTdsData.push(deductee);
                } else if (recordType === 'SD') {
                    const recordNo = parts[3] || String(newSalaryData.length + 1);
                    const totalGross = parseFloat(parts[12] || 0);
                    const currentGross = parseFloat(parts[34] || totalGross);
                    const sec17_1 = parseFloat(parts[67] || currentGross);
                    const currentTds = parseFloat(parts[36] || parts[28] || 0);

                    const salaryRow = {
                        id: Date.now() + rowIndex++,
                        recordNo: recordNo,
                        employeePan: parts[6] || '',
                        employeeRef: parts[7] || '',
                        employeeName: parts[8] || '',
                        category: parts[9] || 'G',
                        periodFrom: parseDateDDMMYYYY(parts[10]),
                        periodTo: parseDateDDMMYYYY(parts[11]),
                        totalGrossSalary: totalGross,
                        grossSalary: currentGross,
                        salary17_1: sec17_1,
                        valPerq17_2: parseFloat(parts[68] || 0),
                        profits17_3: parseFloat(parts[69] || 0),
                        travelConcession: parseFloat(parts[70] || 0),
                        gratuity10_10: parseFloat(parts[71] || 0),
                        pension10_10A: parseFloat(parts[72] || 0),
                        leaveEncashment10_10AA: parseFloat(parts[73] || 0),
                        hra: parseFloat(parts[74] || 0),
                        specialAllowance10_14: parseFloat(parts[75] || 0),
                        otherExemptions: parseFloat(parts[76] || 0),
                        totalExemptions10: parseFloat(parts[77] || 0),
                        standardDed16ia: parseFloat(parts[15] || 0),
                        entertainmentAllow16ii: 0,
                        professionalTax16iii: 0,
                        deductions16: parseFloat(parts[15] || 0),
                        incomeChargeable: parseFloat(parts[16] || 0),
                        incomeOtherSources: parseFloat(parts[17] || 0),
                        grossTotalIncome: parseFloat(parts[18] || 0),
                        chapterVIA: parseFloat(parts[21] || 0),
                        taxableIncCurrent: parseFloat(parts[22] || 0) - parseFloat(parts[35] || 0),
                        taxableAmtPrevEmp: parseFloat(parts[35] || 0),
                        totalTaxableIncome: parseFloat(parts[22] || 0),
                        incomeTax: parseFloat(parts[23] || 0),
                        rebate87A: 0,
                        surcharge: parseFloat(parts[24] || 0),
                        cess: parseFloat(parts[25] || 0),
                        relief89: parseFloat(parts[26] || 0),
                        netTaxPayable: parseFloat(parts[27] || 0),
                        tdsCurrentEmp: currentTds,
                        tdsPrevEmp: parseFloat(parts[37] || 0),
                        totalTdsDeducted: parseFloat(parts[28] || 0),
                        shortfallExcess: parseFloat(parts[29] || 0),
                        regime115BAC: parts[30] === 'Y' ? 'Y' : 'N',
                        higherRateTax: parts[38] || 'N',
                        rentExceedsThreshold: parts[39] || 'N',
                        interestPaidHouseProperty: parts[49] || 'N',
                        superannuationFundPaid: parts[59] || 'N',
                        otherTdsTcs192_2B: parseFloat(parts[80] || 0),
                        source: 'conso'
                    };
                    currentSalary = salaryRow;
                    salaryRecordMap[recordNo] = salaryRow;
                    newSalaryData.push(salaryRow);
                } else if (recordType === 'S16') {
                    const targetRecordNo = parts[3];
                    const targetSalary = salaryRecordMap[targetRecordNo] || currentSalary;
                    if (targetSalary) {
                        const secId = (parts[5] || '').toLowerCase().trim();
                        const amt = parseFloat(parts[6] || 0);
                        if (secId.includes('ia') || secId === '16(ia)') {
                            targetSalary.standardDed16ia = amt;
                        } else if (secId.includes('ii') || secId === '16(ii)') {
                            targetSalary.entertainmentAllow16ii = amt;
                        } else if (secId.includes('iii') || secId === '16(iii)') {
                            targetSalary.professionalTax16iii = amt;
                        }
                        targetSalary.deductions16 = (targetSalary.standardDed16ia || 0) +
                            (targetSalary.entertainmentAllow16ii || 0) +
                            (targetSalary.professionalTax16iii || 0);
                    }
                } else if (recordType === 'C6A') {
                    const targetRecordNo = parts[3];
                    const targetSalary = salaryRecordMap[targetRecordNo] || currentSalary;
                    if (targetSalary) {
                        const secCode = (parts[5] || '').toUpperCase().trim();
                        const deductible = parseFloat(parts[6] || 0);
                        const gross = parseFloat(parts[7] || 0);
                        const qual = parseFloat(parts[8] || 0);

                        if (secCode === '80C') { targetSalary.sec80C = deductible; targetSalary.sec80C_Gross = gross; }
                        else if (secCode === '80CCC') { targetSalary.sec80CCC = deductible; targetSalary.sec80CCC_Gross = gross; }
                        else if (secCode === '80CCD(1)' || secCode === '80CCD1') { targetSalary.sec80CCD1 = deductible; targetSalary.sec80CCD1_Gross = gross; }
                        else if (secCode === '80CCD(1B)' || secCode === '80CCD1B') { targetSalary.sec80CCD1B = deductible; targetSalary.sec80CCD1B_Gross = gross; }
                        else if (secCode === '80CCD(2)' || secCode === '80CCD2') { targetSalary.sec80CCD2 = deductible; targetSalary.sec80CCD2_Gross = gross; }
                        else if (secCode === '80CCG') { targetSalary.sec80CCG = deductible; }
                        else if (secCode === '80D') { targetSalary.sec80D = deductible; targetSalary.sec80D_Gross = gross; }
                        else if (secCode === '80E') { targetSalary.sec80E = deductible; targetSalary.sec80E_Gross = gross; }
                        else if (secCode === '80G') { targetSalary.sec80G = deductible; targetSalary.sec80G_Gross = gross; targetSalary.sec80G_Qual = qual; }
                        else if (secCode === '80TTA') { targetSalary.sec80TTA = deductible; targetSalary.sec80TTA_Gross = gross; targetSalary.sec80TTA_Qual = qual; }
                        else { targetSalary.sec80Others = deductible; targetSalary.sec80Others_Gross = gross; targetSalary.sec80Others_Qual = qual; }
                    }
                } else if (recordType === '94P') {
                    const senior = {
                        id: Date.now() + rowIndex++,
                        recordNo: parts[3] || String(newSeniorCitizenData.length + 1),
                        seniorCitizenPan: parts[5] || '',
                        seniorCitizenName: parts[6] || '',
                        category: parts[7] || 'S',
                        regime115BAC: parts[8] || 'N',
                        grossPension17_1ii: parseFloat(parts[9] || 0),
                        totalDed16: parseFloat(parts[11] || 0),
                        salariesIncome: parseFloat(parts[12] || 0),
                        interestIncome: parseFloat(parts[13] || 0),
                        grossTotalIncome: parseFloat(parts[14] || 0),
                        aggChapterVIA: parseFloat(parts[18] || 0),
                        totalTaxableIncome: parseFloat(parts[19] || 0),
                        incomeTax: parseFloat(parts[20] || 0),
                        taxPayable: parseFloat(parts[24] || 0),
                        netTaxPayable: parseFloat(parts[26] || 0),
                        source: 'conso'
                    };
                    newSeniorCitizenData.push(senior);
                }
            });

            // Post-process Challans to auto-fill Section Code from their Deductees
            newChallans.forEach(challan => {
                const matchingDeductee = newTdsData.find(tds => tds.challanId === challan.id);
                if (matchingDeductee && matchingDeductee.sectionCode) {
                    if (!challan.sectionCode) {
                        challan.sectionCode = matchingDeductee.sectionCode;
                    }
                }
            });

            // Save and update Challan Data
            setChallanData(newChallans);
            setStorageItem('tds24QChallanData', newChallans);
            setOriginalChallanData(JSON.parse(JSON.stringify(newChallans)));
            setStorageItem('tds24QOriginalChallanData', newChallans);

            // Save and update Deductee Data
            setTdsData(newTdsData);
            setStorageItem('tds24QTdsData', newTdsData);
            setOriginalTdsData(JSON.parse(JSON.stringify(newTdsData)));
            setStorageItem('tds24QOriginalTdsData', newTdsData);

            // Save and update Salary Data
            setSalaryData(newSalaryData);
            setStorageItem('tds24QSalaryData', newSalaryData);
            setOriginalSalaryData(JSON.parse(JSON.stringify(newSalaryData)));
            setStorageItem('tds24QOriginalSalaryData', newSalaryData);

            // Save and update Senior Citizen Data
            setSeniorCitizenData(newSeniorCitizenData);
            setStorageItem('tds24QSeniorCitizenData', newSeniorCitizenData);
            setOriginalSeniorCitizenData(JSON.parse(JSON.stringify(newSeniorCitizenData)));
            setStorageItem('tds24QOriginalSeniorCitizenData', newSeniorCitizenData);

            toast.success(`Conso File Uploaded Successfully! (${newChallans.length} Challans, ${newTdsData.length} Deductees, ${newSalaryData.length} Salaries)`);
        } catch (err) {
            console.error('Error parsing 24Q Q4 conso file:', err);
            toast.error('Error parsing conso file');
        }
    };

    const handleConsoFileUpload = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const content = ev.target.result;
            parseConsoFile(content);
        };
        reader.readAsText(file);
    };

    useEffect(() => {
        if (profileData?.section3?.sameAsAbove && isProfileEditing) {
            const newData = { ...profileData }
            newData.section3.nameOfPremises = newData.section2.nameOfPremises || ''
            newData.section3.flatNo = newData.section2.flatNo || ''
            newData.section3.areaLocality = newData.section2.areaLocality || ''
            newData.section3.roadStreet = newData.section2.roadStreet || ''
            newData.section3.townCity = newData.section2.townCity || ''
            newData.section3.pinCode = newData.section2.pinCode || ''
            newData.section3.state = newData.section2.state || ''
            newData.section3.email = newData.section2.email || ''
            setProfileData(newData)
            localStorage.setItem('tds24QProfileData', JSON.stringify(newData))
        }
    }, [profileData?.section2, profileData?.section3?.sameAsAbove])

    const handleProfileInputChange = (field, value) => {
        const keys = field.split('.')
        const newData = { ...profileData }
        let current = newData
        for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]]
        }
        current[keys[keys.length - 1]] = value

        if (field === 'section3.sameAsAbove' && value === true) {
            newData.section3.nameOfPremises = newData.section2.nameOfPremises || ''
            newData.section3.flatNo = newData.section2.flatNo || ''
            newData.section3.areaLocality = newData.section2.areaLocality || ''
            newData.section3.roadStreet = newData.section2.roadStreet || ''
            newData.section3.townCity = newData.section2.townCity || ''
            newData.section3.pinCode = newData.section2.pinCode || ''
            newData.section3.state = newData.section2.state || ''
            newData.section3.email = newData.section2.email || ''
        }

        setProfileData(newData)
        localStorage.setItem('tds24QProfileData', JSON.stringify(newData))
    }

    function validateDataForReturn() {
        const errors = []

        if (!tdsData || tdsData.length === 0) errors.push('At least one TDS entry is required')
        if (!challanData || challanData.length === 0) errors.push('At least one Challan entry is required')

        // Receipt No. validation (only when isEarlierReturnFiled = Yes)
        if (profileData?.section1?.isEarlierReturnFiled === 'Yes' && !profileData?.section1?.receiptNoOriginal) {
            errors.push('Receipt No. of Original return is required (or set "Is Earlier Return Filed" to No)')
        }

        // Profile Section 2 mandatory fields
        if (!profileData?.section2?.name) errors.push('Name is required in Profile Section 2')
        if (!profileData?.section2?.flatNo) errors.push('Flat No. is required in Profile Section 2')
        if (!profileData?.section2?.state) errors.push('State is required in Profile Section 2')
        if (!profileData?.section2?.pinCode) errors.push('PIN Code is required in Profile Section 2')

        // Profile Section 3 (Responsible Person) mandatory fields
        if (!profileData?.section3?.name) errors.push('Responsible Person Name is required in Profile')
        if (!profileData?.section3?.designation) errors.push('Responsible Person Designation is required in Profile')
        if (!profileData?.section3?.flatNo && !profileData?.section3?.nameOfPremises && !profileData?.section3?.roadStreet) {
            errors.push('Responsible Person Address is required in Profile')
        }
        if (!profileData?.section3?.state) errors.push('Responsible Person State is required in Profile')
        if (!profileData?.section3?.pinCode) errors.push('Responsible Person Pincode is required in Profile')
        if (!profileData?.section3?.email) errors.push('Responsible Person Email is required in Profile')
        if (!profileData?.section3?.mobileNo) errors.push('Responsible Person Mobile is required in Profile')
        if (!profileData?.section3?.permanentAccountNumber) errors.push('Responsible Person PAN is required in Profile')

        // TDS entry field-level validation
        if (tdsData && tdsData.length > 0) {
            tdsData.forEach((row, index) => {
                if (!row.panOfDeductee) errors.push(`TDS Entry ${index + 1}: PAN of Deductee is required`)
                if (!row.sectionCode) errors.push(`TDS Entry ${index + 1}: Section Code is required`)
                if (row.tdsAmount === '' || row.tdsAmount === null || row.tdsAmount === undefined) {
                    errors.push(`TDS Entry ${index + 1}: TDS Amount is required`)
                }
                if (!row.dateOfPayment) errors.push(`TDS Entry ${index + 1}: Date of Payment is required`)
                if (!row.dateOfDeduction && (parseFloat(row.tdsAmount || 0) + parseFloat(row.surcharge || 0) + parseFloat(row.cess || 0)) > 0) errors.push(`TDS Entry ${index + 1}: Date of Deduction is required`)
            })
        }

        // Challan entry field-level validation
        if (challanData && challanData.length > 0) {
            challanData.forEach((row, index) => {
                if (!row.bsrCode) errors.push(`Challan Entry ${index + 1}: BSR Code is required`)
                else if (row.bsrCode.length !== 7) errors.push(`Challan Entry ${index + 1}: BSR Code must be 7 digits`)
                if (!row.challanSerialNo) errors.push(`Challan Entry ${index + 1}: Challan Serial No. is required`)
                else if (row.challanSerialNo.length !== 5) errors.push(`Challan Entry ${index + 1}: Challan Serial No. must be 5 digits`)
                if (!row.dateOfDeposit) errors.push(`Challan Entry ${index + 1}: Date of Deposit is required`)
            })
        }

        return errors
    }

    function generateTdsStatementFile() {
        const currentDate = new Date()
        const fileCreationDate = currentDate.toLocaleDateString('en-GB').replace(/\//g, '')

        let fyStartYear = 0
        let financialYear = ''
        if (selectedFinancialYear) {
            const parts = selectedFinancialYear.split('-')
            if (parts.length >= 2) {
                fyStartYear = parseInt(parts[0])
                const yearPart2 = parts[1].length === 4 ? parts[1].slice(-2) : parts[1]
                financialYear = `${parts[0]}${yearPart2}`
            }
        }
        if (!fyStartYear || isNaN(fyStartYear)) {
            const today = new Date()
            fyStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1
            financialYear = `${fyStartYear}${String(fyStartYear + 1).slice(-2)}`
        }
        const ayStart = fyStartYear + 1
        const assessmentYear = `${ayStart}${String(ayStart + 1).slice(-2)}`




        const formatMinorHead = (val) => {
            if (!val) return ''
            const s = String(val).trim()
            if (s === '200' || s === '400') return s
            if (s.toLowerCase().includes('payable') || s.toLowerCase().includes('taxpayer')) return '200'
            if (s.toLowerCase().includes('regular') || s.toLowerCase().includes('assessment')) return '400'
            const match = s.match(/(200|400)/)
            return match ? match[0] : s
        }



        const section1 = profileData?.section1 || {}
        const section2 = profileData?.section2 || {}
        const section3 = profileData?.section3 || {}

        let fileContent = ''
        let lineNumber = 1

        // File Header (FH)
        const fh = []
        fh[1] = lineNumber // 1. Line Number
        fh[2] = 'FH' // 2. Record Type
        fh[3] = 'SL1' // 3. File Type
        fh[4] = 'R' // 4. Upload Type
        fh[5] = fileCreationDate // 5. File Creation Date
        fh[6] = '1' // 6. File Sequence No.
        fh[7] = 'D' // 7. Uploader Type (D for Deductor)
        fh[8] = section1.taxDeduction || '' // 8. TAN of Employer
        fh[9] = '1' // 9. Total No. of Batches
        fh[10] = 'Tax Eases Client' // 10. Name of RPU
        fh[11] = '' // 11. Record Hash
        fh[12] = '' // 12. FVU Version
        fh[13] = '' // 13. File Hash
        fh[14] = '' // 14. Sam Version
        fh[15] = '' // 15. SAM Hash
        fh[16] = '' // 16. SCM Version
        fh[17] = '' // 17. SCM Hash
        fh[18] = '' // 18. Consolidated file hash
        fileContent += fh.slice(1).join('^') + '\n'; lineNumber++

        // Batch Header (BH)
        const batchTotal = challanData.reduce((sum, c) => sum + parseFloat(c.totalAmount || 0), 0)
        const deductorAddr1 = `${section2.flatNo || ''} ${section2.nameOfPremises || ''} ${section2.roadStreet || ''}`.trim()
        const respAddr1 = `${section3.flatNo || ''} ${section3.nameOfPremises || ''} ${section3.roadStreet || ''}`.trim()
        const receiptNo7 = ''
        const receiptNo8 = section1.isEarlierReturnFiled === 'Yes' ? (section1.receiptNoOriginal || '') : ''

        const bh = []
        bh[1] = lineNumber // 1. Line Number
        bh[2] = 'BH' // 2. Record Type
        bh[3] = '1' // 3. Batch Number
        bh[4] = challanData.length // 4. Count of Challan Records
        const isNewFormat = fyStartYear >= 2026;
        bh[5] = isNewFormat ? '138' : '24Q' // 5. Form Number
        bh[6] = '' // 6. Transaction Type
        bh[7] = '' // 7. Batch Updation Indicator
        bh[8] = receiptNo7 // 8. Original Token Number
        bh[9] = receiptNo8 // 9. Token no. of prev regular statement
        bh[10] = '' // 10. Token Number of submitted statement
        bh[11] = '' // 11. Token Number date
        bh[12] = '' // 12. Last TAN of Deductor
        bh[13] = section1.taxDeduction || '' // 13. TAN of Deductor
        bh[14] = '' // 14. Receipt number (eight digit)
        bh[15] = section1.permanentAccountNumber || '' // 15. PAN of Deductor
        bh[16] = assessmentYear // 16. Assessment Yr
        bh[17] = financialYear // 17. Financial Yr
        bh[18] = 'Q4' // 18. Period
        bh[19] = padString(section2.name || '', 75) // 19. Name of Employer
        bh[20] = padString(section2.branchDivision || '', 75) // 20. Branch/ Division
        bh[21] = padString(deductorAddr1, 25) // 21. Address1
        bh[22] = padString(section2.areaLocality || '', 25) // 22. Address2
        bh[23] = padString(section2.townCity || '', 25) // 23. Address3
        bh[24] = '' // 24. Address4
        bh[25] = '' // 25. Address5
        bh[26] = getStateCode(section2.state) // 26. State
        bh[27] = section2.pinCode || '' // 27. PIN
        bh[28] = padString(section2.email || '', 75) // 28. Email ID
        bh[29] = section2.stdCode || '' // 29. STD code
        bh[30] = section2.telephoneNo || '' // 30. Tel-Phone No.
        bh[31] = 'N' // 31. Change of Address of employer
        bh[32] = section1.typeOfDeduction || '' // 32. Deductor Type
        bh[33] = padString(section3.name || '', 75) // 33. Name of Responsible Person
        bh[34] = padString(section3.designation || '', 20) // 34. Designation
        bh[35] = padString(respAddr1, 25) // 35. Responsible Person's Address1
        bh[36] = padString(section3.areaLocality || '', 25) // 36. Address2
        bh[37] = padString(section3.townCity || '', 25) // 37. Address3
        bh[38] = '' // 38. Address4
        bh[39] = '' // 39. Address5
        bh[40] = getStateCode(section3.state) // 40. State
        bh[41] = section3.pinCode || '' // 41. PIN
        bh[42] = padString(section3.email || '', 75) // 42. Email ID
        bh[43] = section3.mobileNo || '' // 43. Mobile number
        bh[44] = section3.telephone || '' // 44. STD Code
        bh[45] = section3.telephoneAlternate || '' // 45. Tel-Phone No.
        bh[46] = section3.hasAddressChanged === 'Yes' ? 'Y' : 'N' // 46. Change of Address of Responsible person
        bh[47] = formatDecimalAmount(batchTotal) // 47. Batch Total - Deposit Amount
        bh[48] = '' // 48. Unmatched challan count
        // Fields 49 & 50 are linked exclusively to SD (Salary Detail) records
        // BH Field 50 = sum of SD Field 19 for each row, using the exact same formula as SD generation:
        //   SD F19 = (F13: grossSalary+taxableAmtPrevEmp) - (F16: sec16 deductions) - (F76: totalExemptions10) + (F18: incomeOtherSources) + (F77: 0)
        const sdRecordCount = salaryData ? salaryData.length : 0
        const sdGrossTotalIncome = salaryData ? salaryData.reduce((s, r) => {
            const f13 = parseFloat(r.grossSalary || 0) + parseFloat(r.taxableAmtPrevEmp || 0)
            const f16 = parseFloat(r.standardDed16ia || 0) + parseFloat(r.entertainmentAllow16ii || 0) + parseFloat(r.professionalTax16iii || 0)
            const f76 = parseFloat(r.totalExemptions10 || 0)
            const f17 = f13 - f16 - f76
            const f18 = parseFloat(r.incomeOtherSources || 0)
            const f19 = f17 + f18 // F77 is always 0.00
            return s + f19
        }, 0) : 0
        bh[49] = sdRecordCount // 49. Count of Salary Details (SD) Records
        bh[50] = formatDecimalAmount(sdGrossTotalIncome) // 50. Batch Total - Sum of SD Field 19 (Gross Total Income)
        bh[51] = 'N' // 51. AO Approval
        bh[52] = section1.isEarlierReturnFiled === 'Yes' ? 'Y' : 'N' // 52. Regular statement filed for earlier period
        bh[53] = '' // 53. Last Deductor Type
        bh[54] = '' // 54. State Name
        bh[55] = '' // 55. PAO Code
        bh[56] = '' // 56. DDO Code
        bh[57] = '' // 57. Ministry Name
        bh[58] = '' // 58. Ministry Name Other
        bh[59] = section3.permanentAccountNumber || '' // 59. PAN of Responsible Person
        bh[60] = '' // 60. PAO Registration No
        bh[61] = '' // 61. DDO Registration No
        bh[62] = '' // 62. STD code (Alternate)
        bh[63] = '' // 63. Tel-Phone No. (Alternate)
        bh[64] = '' // 64. Email ID (Alternate)
        bh[65] = '' // 65. STD Code (Alternate)
        bh[66] = '' // 66. Tel-Phone No. (Alternate)
        bh[67] = '' // 67. Email ID (Alternate)
        bh[68] = '' // 68. AIN
        bh[69] = section2.gstin || '' // 69. GSTN
        bh[70] = seniorCitizenData ? seniorCitizenData.length : 0 // 70. Count of Section 194P Details
        bh[71] = formatDecimalAmount(seniorCitizenData ? seniorCitizenData.reduce((s, r) => s + parseFloat(r.grossTotalIncome || 0), 0) : 0) // 71. Batch Total - Gross Total Income (194P)
        bh[72] = '' // 72. Record Hash
        fileContent += bh.slice(1).join('^') + '\n'; lineNumber++

        // Build a global PAN -> Serial No. map for Field 7 of DD records
        // Each unique PAN gets a unique sequential Employee Serial No.
        // Entries with missing/invalid PAN also get a serial, keyed by their internal ID
        const panSerialMap = new Map()
        let serialCounter = 1
        tdsData.forEach(entry => {
            const pan = (entry.panOfDeductee || '').trim().toUpperCase()
            const key = pan || `NO_PAN_${entry.id}` // fallback key for blank/invalid PANs
            if (!panSerialMap.has(key)) {
                panSerialMap.set(key, serialCounter++)
            }
        })

        // Challan Detail Records (CD) + Deductee Records (DD)
        let actualChallanIndex = 1
        challanData.forEach((challan) => {
            let tdsEntries = tdsData.filter(t => t.challanId == challan.id)

            if (selectedReturnType === 'Correction') {
                tdsEntries = tdsEntries.filter(tds => {
                    const original = originalTdsData.find(o => o.id === tds.id)
                    return !original || JSON.stringify(tds) !== JSON.stringify(original)
                })
                if (tdsEntries.length === 0) return
            }

            const cd = []
            const eduCess = parseFloat(challan.educationCess || 0) + parseFloat(challan.higherEducationCess || 0)
            const deducteeTotal = tdsEntries.reduce((s, t) => s + parseFloat(t.tdsAmount || 0), 0)

            cd[1] = lineNumber // 1. Line Number
            cd[2] = 'CD' // 2. Record Type
            cd[3] = '1' // 3. Batch Number
            cd[4] = actualChallanIndex // 4. Challan-Detail Record Number
            cd[5] = tdsEntries.length // 5. Count of Deductee Records
            cd[6] = 'N' // 6. NIL Challan Indicator
            cd[7] = '' // 7. Challan Updation Indicator
            cd[8] = '' // 8. Filler 2
            cd[9] = '' // 9. Filler 3
            cd[10] = '' // 10. Filler 4
            cd[11] = '' // 11. Last Challan Serial No.
            cd[12] = challan.challanSerialNo || '' // 12. Challan Serial No.
            cd[13] = '' // 13. Last DDO Serial No.
            cd[14] = '' // 14. DDO Serial No.
            cd[15] = '' // 15. Last Bank-Branch Code
            cd[16] = challan.bsrCode || '' // 16. Bank-Branch Code
            cd[17] = '' // 17. Last Date of Challan
            cd[18] = formatDate(challan.dateOfDeposit) // 18. Date of Challan
            cd[19] = '' // 19. Filler 5
            cd[20] = '' // 20. Filler 6
            cd[21] = '' // 21. Section
            cd[22] = formatDecimalAmount(challan.tdsAmount) // 22. Oltas TDS Income Tax
            cd[23] = formatDecimalAmount(challan.surcharge) // 23. Oltas TDS Surcharge
            cd[24] = formatDecimalAmount(eduCess) // 24. Oltas TDS Cess
            cd[25] = formatDecimalAmount(challan.interest) // 25. Oltas TDS Interest
            cd[26] = formatDecimalAmount(challan.other) // 26. Oltas TDS Others
            cd[27] = formatDecimalAmount(challan.totalAmount) // 27. Total of Deposit Amount
            cd[28] = '' // 28. Last Total of Deposit
            cd[29] = formatDecimalAmount(deducteeTotal) // 29. Total Tax Deposit Amount (as per annexure)
            cd[30] = formatDecimalAmount(deducteeTotal) // 30. TDS Income Tax
            cd[31] = formatDecimalAmount(0) // 31. TDS Surcharge
            cd[32] = formatDecimalAmount(0) // 32. TDS Cess
            cd[33] = formatDecimalAmount(deducteeTotal) // 33. Sum of Total Income Tax Deducted
            cd[34] = formatDecimalAmount(0) // 34. TDS Interest
            cd[35] = formatDecimalAmount(0) // 35. TDS Others
            cd[36] = '' // 36. Cheque / DD No.
            cd[37] = challan.bookEntry ? 'Y' : 'N' // 37. By Book entry / Cash
            cd[38] = '' // 38. Remarks
            cd[39] = formatDecimalAmount(challan.feesAmount) // 39. Fee
            cd[40] = formatMinorHead(challan.minorHead) // 40. Minor Head of Challan
            cd[41] = '' // 41. Record Hash
            fileContent += cd.slice(1).join('^') + '\n'; lineNumber++

            // Deductee records
            tdsEntries.forEach((tds, tdsIndex) => {
                const dd = []
                let deducteeCode = tds.deducteeCode || '02'
                if (deducteeCode === '01') deducteeCode = '1'
                if (deducteeCode === '02') deducteeCode = '2'
                const tdsVal = parseFloat(tds.tdsAmount || 0)
                const surchargeVal = parseFloat(tds.surcharge || 0)
                const cessVal = parseFloat(tds.cess || 0)
                const totalTax = tdsVal + surchargeVal + cessVal
                const refNo = (tds.deducteeReferenceNo || '').toString()

                dd[1] = lineNumber // 1. Line Number
                dd[2] = 'DD' // 2. Record Type
                dd[3] = '1' // 3. Batch Number
                dd[4] = actualChallanIndex // 4. Challan-Detail Record Number
                dd[5] = tdsIndex + 1 // 5. Deductee Record No
                dd[6] = 'O' // 6. Mode
                dd[7] = String(panSerialMap.get((tds.panOfDeductee || '').trim().toUpperCase() || `NO_PAN_${tds.id}`) || tdsIndex + 1).padStart(9, '0') // 7. Employee Serial No.
                dd[8] = '' // 8. Deductee Code
                dd[9] = '' // 9. Last Employee PAN
                dd[10] = tds.panOfDeductee || '' // 10. Employee PAN
                dd[11] = '' // 11. Last PAN Ref No.
                dd[12] = '' // 12. PAN Ref. No.
                dd[13] = `${tds.firstName || ''} ${tds.middleName || ''} ${tds.lastName || ''}`.trim() // 13. Name of Employee
                dd[14] = formatDecimalAmount(tdsVal) // 14. TDS Income Tax
                dd[15] = formatDecimalAmount(surchargeVal) // 15. TDS Surcharge
                dd[16] = formatDecimalAmount(cessVal) // 16. TDS Cess
                dd[17] = formatDecimalAmount(totalTax) // 17. Total Income Tax Deducted
                dd[18] = '' // 18. Last Total Tax Deducted
                dd[19] = formatDecimalAmount(totalTax) // 19. Total Tax Deposited
                dd[20] = '' // 20. Last Total Tax Deposited
                dd[21] = '' // 21. Total Value of Purchase
                dd[22] = formatDecimalAmount(tds.amountOfPayment) // 22. Amount Paid / Credited
                dd[23] = formatDate(tds.dateOfPayment) // 23. Date of payment / credit
                dd[24] = totalTax > 0 ? formatDate(tds.dateOfDeduction) : '' // 24. Date on which tax Deducted
                dd[25] = formatDate(challan.dateOfDeposit) // 25. Date of Deposit
                dd[26] = '' // 26. Rate at which Tax Deducted
                dd[27] = '' // 27. Grossing up Indicator
                dd[28] = '' // 28. Book Entry / Cash Indicator
                dd[29] = '' // 29. Date of furnishing Certificate
                dd[30] = tds.reasonForNonDeduction || '' // 30. Remarks 1 (Reason for non-deduction)
                dd[31] = '' // 31. Remarks 2
                dd[32] = '' // 32. Remarks 3
                dd[33] = tds.sectionCode || '' // 33. Section Code
                dd[34] = tds.certificateNo197 || '' // 34. Certificate number issued u/s 197
                dd[35] = '' // 35. Filler 1
                dd[36] = '' // 36. Filler 2
                dd[37] = '' // 37. Filler 3
                dd[38] = '' // 38. Filler 4
                dd[39] = '' // 39. Filler 5
                dd[40] = '' // 40. Filler 6
                dd[41] = '' // 41. Filler 7
                dd[42] = '' // 42. Filler 8
                dd[43] = '' // 43. Filler 9
                dd[44] = '' // 44. Filler 10
                dd[45] = '' // 45. Filler 11
                dd[46] = '' // 46. Filler 12
                dd[47] = '' // 47. Filler 13
                dd[48] = '' // 48. Filler 14
                dd[49] = '' // 49. Filler 15
                dd[50] = '' // 50. Filler 16
                dd[51] = '' // 51. Filler 17
                dd[52] = '' // 52. Filler 18
                dd[53] = '' // 53. Filler 19
                dd[54] = '' // 54. Record Hash
                fileContent += dd.slice(1).join('^') + '\n'; lineNumber++
            })
            actualChallanIndex++
        })
        // Salary Detail Records (SD) - For 24Q Q4 only
        let sdEntriesToReport = salaryData || [];

        // Check if originalSalaryData is defined in scope, if not default to empty array
        if (selectedReturnType === 'Correction') {
            sdEntriesToReport = sdEntriesToReport.filter(sd => {
                const original = originalSalaryData.find(o => o.employeePan === sd.employeePan)
                if (!original) return true;

                // Robust comparison - include if any key financial field changed
                const hasChanged =
                    parseFloat(sd.grossSalary || 0) !== parseFloat(original.grossSalary || 0) ||
                    parseFloat(sd.totalTaxableIncome || 0) !== parseFloat(original.totalTaxableIncome || 0) ||
                    parseFloat(sd.totalTdsDeducted || 0) !== parseFloat(original.totalTdsDeducted || 0) ||
                    sd.employeeName !== original.employeeName ||
                    sd.category !== original.category;

                return hasChanged;
            })
        }

        if (sdEntriesToReport && sdEntriesToReport.length > 0) {
            sdEntriesToReport.forEach((sdItem, sdIndex) => {
                const sd = []
                const grossCurrent = parseFloat(sdItem.grossSalary || 0)
                const taxablePrev = parseFloat(sdItem.taxableAmtPrevEmp || 0)
                const netChargeableCurrent = parseFloat(sdItem.incomeChargeable || 0)

                const sec16Details = []
                if (parseFloat(sdItem.standardDed16ia || 0) > 0) sec16Details.push({ id: '16(ia)', amount: sdItem.standardDed16ia })
                if (parseFloat(sdItem.entertainmentAllow16ii || 0) > 0) sec16Details.push({ id: '16(ii)', amount: sdItem.entertainmentAllow16ii })
                if (parseFloat(sdItem.professionalTax16iii || 0) > 0) sec16Details.push({ id: '16(iii)', amount: sdItem.professionalTax16iii })

                const c6aDetails = []
                // Sections with no gross input column → gross must be null/empty in C6A record
                const noGrossSections = new Set(['sec80CCG'])
                // 80CCE and 80CCF are removed — 80CCE is the aggregate ceiling (not separately reported)
                const sections = [
                    { id: '80CCG', key: 'sec80CCG' },     // No Gross column — must be first per NSDL spec
                    { id: 'OTHERS', key: 'sec80Others' },
                    { id: '80C', key: 'sec80C' },
                    { id: '80CCC', key: 'sec80CCC' },
                    { id: '80CCD(1)', key: 'sec80CCD1' },
                    { id: '80CCD(1B)', key: 'sec80CCD1B' },
                    { id: '80CCD(2)', key: 'sec80CCD2' },
                    { id: '80D', key: 'sec80D' },
                    { id: '80E', key: 'sec80E' },
                    { id: '80G', key: 'sec80G' },
                    { id: '80TTA', key: 'sec80TTA' }
                ]

                sections.forEach(sec => {
                    const ded = parseFloat(sdItem[sec.key] || 0)
                    if (ded > 0) {
                        const hasQualifying = ['sec80G', 'sec80TTA', 'sec80Others'].includes(sec.key)
                        const hasGross = !noGrossSections.has(sec.key)
                        c6aDetails.push({
                            id: sec.id,
                            deductible: ded,
                            // Gross = null for sections with no gross input column (80CCG)
                            gross: hasGross ? parseFloat(sdItem[`${sec.key}_Gross`] || 0) : null,
                            // Qualifying = null for sections with no qualifying limit
                            qualifying: hasQualifying
                                ? parseFloat(sdItem[`${sec.key}_Qual`] || 0)
                                : null
                        })
                    }
                })

                sd[1] = lineNumber // 1. Line Number
                sd[2] = 'SD' // 2. Record Type
                sd[3] = '1' // 3. Batch Number
                sd[4] = sdIndex + 1 // 4. Salary Details Record No
                sd[5] = 'A' // 5. Mode
                sd[6] = '' // 6. Filler7
                sd[7] = sdItem.employeePan || '' // 7. Employee PAN
                sd[8] = '' // 8. PAN Ref. No.
                sd[9] = sdItem.employeeName || '' // 9. Name of Employee
                sd[10] = sdItem.category || 'O' // 10. Category of Employee
                sd[11] = formatDate(sdItem.periodFrom) // 11. Period of Employment From
                sd[12] = formatDate(sdItem.periodTo) // 12. Period of Employment To
                sd[13] = formatDecimalAmount(grossCurrent + taxablePrev) // 13. Total amount of salary
                sd[14] = '' // 14. Filler 8
                sd[15] = sec16Details.length // 15. Count of Sec 16 Detail (Standard ded, Ent. allow, Prof tax)
                const totalDed16 = parseFloat(sdItem.standardDed16ia || 0) +
                    parseFloat(sdItem.entertainmentAllow16ii || 0) +
                    parseFloat(sdItem.professionalTax16iii || 0)
                sd[16] = formatDecimalAmount(totalDed16) // 16. Total Deduction u/s 16
                // Field 17 = Field 13 - Field 16 - Field 76
                const field13Val = parseFloat(grossCurrent + taxablePrev) || 0
                const field16Val = totalDed16
                const field76Val = parseFloat(sdItem.totalExemptions10 || 0)
                const field17Val = field13Val - field16Val - field76Val
                sd[17] = formatDecimalAmount(field17Val) // 17. Income chargeable under head Salaries
                sd[18] = formatDecimalAmount(sdItem.incomeOtherSources) // 18. Income under any head other than Salaries
                // Field 19 = Field 17 + Field 18 + Field 77
                const field18Val = parseFloat(sdItem.incomeOtherSources || 0)
                const field77Val = 0.00 // Field 77 is fixed at 0.00
                const field19Val = field17Val + field18Val + field77Val
                sd[19] = formatDecimalAmount(field19Val) // 19. Gross Total Income
                sd[20] = '' // 20. Last Gross Total Income
                sd[21] = c6aDetails.length // 21. Count of Chapter VI-A Detail
                // SD[22] uses chapterVIA as calculated and displayed by the UI (sdItem.chapterVIA),
                // rather than re-summing individual fields, to ensure TXT always matches the screen.
                sd[22] = formatDecimalAmount(sdItem.chapterVIA) // 22. Total Deduction under Chapter VI-A
                sd[23] = formatDecimalAmount(sdItem.totalTaxableIncome) // 23. Total Taxable Income
                sd[24] = formatDecimalAmount(sdItem.incomeTax) // 24. Income Tax on Total Income
                sd[25] = formatDecimalAmount(sdItem.surcharge) // 25. Surcharge
                sd[26] = formatDecimalAmount(sdItem.cess) // 26. Education Cess
                sd[27] = formatDecimalAmount(sdItem.relief89) // 27. Relief u/s 89
                sd[28] = formatDecimalAmount(sdItem.netTaxPayable) // 28. Net Income Tax payable
                sd[29] = formatDecimalAmount(sdItem.totalTdsDeducted) // 29. Total Tax deducted for whole year
                sd[30] = formatDecimalAmount(sdItem.shortfallExcess) // 30. Shortfall/Excess
                const agg80C = parseFloat(sdItem.sec80C || 0) + parseFloat(sdItem.sec80CCC || 0) + parseFloat(sdItem.sec80CCD1 || 0)
                sd[31] = formatDecimalAmount(agg80C) // 31. Aggregate deductions u/s 80C, 80CCC, 80CCD(1)
                sd[32] = '' // 32. Remarks (For future use)
                sd[33] = '' // 33. Remarks (For future use)
                sd[34] = formatDecimalAmount(grossCurrent) // 34. Taxable Amount - current employer
                sd[35] = formatDecimalAmount(taxablePrev) // 35. Taxable Amount - previous employer
                sd[36] = formatDecimalAmount(sdItem.tdsCurrentEmp) // 36. TDS by current employer
                sd[37] = formatDecimalAmount(sdItem.tdsPrevEmp) // 37. TDS by previous employer
                sd[38] = sdItem.higherRateTax || 'N' // 38. Tax at Higher rate (PAN non-furnishing)
                sd[39] = sdItem.rentExceedsThreshold || 'N' // 39. Rent payment exceeds threshold
                const landlordPans = [sdItem.landlordPan1, sdItem.landlordPan2, sdItem.landlordPan3, sdItem.landlordPan4].filter(p => !!p)
                sd[40] = landlordPans.length // 40. Count of Landlords
                sd[41] = sdItem.landlordPan1 || '' // 41. PAN of landlord 1
                sd[42] = sdItem.landlordName1 || '' // 42. Name of landlord 1
                sd[43] = sdItem.landlordPan2 || '' // 43. PAN of landlord 2
                sd[44] = sdItem.landlordName2 || '' // 44. Name of landlord 2
                sd[45] = sdItem.landlordPan3 || '' // 45. PAN of landlord 3
                sd[46] = sdItem.landlordName3 || '' // 46. Name of landlord 3
                sd[47] = sdItem.landlordPan4 || '' // 47. PAN of landlord 4
                sd[48] = sdItem.landlordName4 || '' // 48. Name of landlord 4
                sd[49] = sdItem.interestPaidHouseProperty || 'N' // 49. Interest paid to lender
                const lenderPans = [sdItem.lenderPan1, sdItem.lenderPan2, sdItem.lenderPan3, sdItem.lenderPan4].filter(p => !!p)
                sd[50] = lenderPans.length // 50. Count of Lenders
                sd[51] = sdItem.lenderPan1 || '' // 51. PAN of lender 1
                sd[52] = sdItem.lenderName1 || '' // 52. Name of lender 1
                sd[53] = sdItem.lenderPan2 || '' // 53. PAN of lender 2
                sd[54] = sdItem.lenderName2 || '' // 54. Name of lender 2
                sd[55] = sdItem.lenderPan3 || '' // 55. PAN of lender 3
                sd[56] = sdItem.lenderName3 || '' // 56. Name of lender 3
                sd[57] = sdItem.lenderPan4 || '' // 57. PAN of lender 4
                sd[58] = sdItem.lenderName4 || '' // 58. Name of lender 4
                sd[59] = sdItem.superannuationFundPaid || 'N' // 59. Superannuation fund contribution
                sd[60] = sd[59] === 'Y' ? (sdItem.superannuationFundName || '') : '' // 60. Name of fund
                sd[61] = sd[59] === 'Y' ? formatDate(sdItem.superannuationDateFrom) : '' // 61. Date from
                sd[62] = sd[59] === 'Y' ? formatDate(sdItem.superannuationDateTo) : '' // 62. Date to
                sd[63] = sd[59] === 'Y' ? formatDecimalAmount(sdItem.superannuationRepaidAmount) : '' // 63. Amount repaid
                sd[64] = sd[59] === 'Y' ? formatDecimalAmount(sdItem.superannuationAvgRate) : '' // 64. Average rate
                sd[65] = sd[59] === 'Y' ? formatDecimalAmount(sdItem.superannuationTaxDeducted) : '' // 65. Tax deducted
                sd[66] = sd[59] === 'Y' ? formatDecimalAmount(sdItem.superannuationGrossTotal) : '' // 66. Gross total income incl superannuation
                sd[67] = formatDecimalAmount(sdItem.salary17_1) // 67. Gross Salary sec 17(1)
                sd[68] = formatDecimalAmount(sdItem.valPerq17_2) // 68. Value of perquisites 17(2)
                sd[69] = formatDecimalAmount(sdItem.profits17_3) // 69. Profits in lieu of salary 17(3)
                sd[70] = formatDecimalAmount(sdItem.travelConcession) // 70. Travel concession 10(5)
                sd[71] = formatDecimalAmount(sdItem.gratuity10_10) // 71. Death-cum-retirement gratuity 10(10)
                sd[72] = formatDecimalAmount(sdItem.pension10_10A) // 72. Commuted value of pension 10(10A)
                sd[73] = formatDecimalAmount(sdItem.leaveEncashment10_10AA) // 73. Leave salary encashment 10(10AA)
                sd[74] = formatDecimalAmount(sdItem.hra) // 74. HRA 10(13A)
                sd[75] = formatDecimalAmount(sdItem.otherExemptions) // 75. Other exemption u/s 10
                sd[76] = formatDecimalAmount(sdItem.totalExemptions10) // 76. Total exemption u/s 10
                sd[77] = '0.00' // 77. Income other sources (TDS offered)
                sd[78] = formatDecimalAmount(sdItem.rebate87A) // 78. Rebate u/s 87A
                sd[79] = sdItem.regime115BAC || 'N' // 79. Opting for 115BAC (Y/N)
                sd[80] = formatDecimalAmount(sdItem.specialAllowance10_14) // 80. Other special allowances 10(14)
                sd[81] = formatDecimalAmount(sdItem.otherTdsTcs192_2B) // 81. Amount reported as per section 192(2B)
                sd[82] = '' // 82. Filler 10
                sd[83] = '' // 83. Filler 11
                sd[84] = '' // 84. Filler 12
                sd[85] = '' // 85. Filler 13
                sd[86] = '' // 86. Filler 14
                sd[87] = '' // 87. Filler 15
                sd[88] = '' // 88. Record Hash

                fileContent += sd.slice(1).join('^') + '\n'; lineNumber++

                // Added S16 records for each non-zero Section 16 deduction
                sec16Details.forEach((sec16, sIdx) => {
                    const s16 = []
                    s16[1] = lineNumber // 1. Line Number
                    s16[2] = 'S16' // 2. Record Type
                    s16[3] = '1' // 3. Batch Number
                    s16[4] = sdIndex + 1 // 4. Salary Detail Record No
                    s16[5] = sIdx + 1 // 5. Salary Detail - Section 16 Details Record No
                    s16[6] = sec16.id // 6. Section 16 section ID
                    s16[7] = formatDecimalAmount(sec16.amount) // 7. Total Deduction under Section 16
                    s16[8] = '' // 8. Record Hash (Not applicable)

                    fileContent += s16.slice(1).join('^') + '\n'; lineNumber++
                })

                // Added C6A records for each non-zero Chapter VI-A deduction
                c6aDetails.forEach((c6a, cIdx) => {
                    const c6a_rec = []
                    c6a_rec[1] = lineNumber // 1. Line Number
                    c6a_rec[2] = 'C6A' // 2. Record Type
                    c6a_rec[3] = '1' // 3. Batch Number
                    c6a_rec[4] = sdIndex + 1 // 4. Salary Detail Record No
                    c6a_rec[5] = cIdx + 1 // 5. Salary Detail - Chapter VI-A Details Record No
                    c6a_rec[6] = c6a.id // 6. Chapter VI-A Section ID
                    c6a_rec[7] = formatDecimalAmount(c6a.deductible) // 7. Total Deductible amount
                    c6a_rec[8] = c6a.gross !== null ? formatDecimalAmount(c6a.gross) : '' // 8. Total Gross amount (empty if no gross column for this section)
                    c6a_rec[9] = c6a.qualifying !== null ? formatDecimalAmount(c6a.qualifying) : '' // 9. Total Qualifying amount (empty if no qualifying limit for this section)
                    c6a_rec[10] = '' // 10. Record Hash (Not applicable)

                    fileContent += c6a_rec.slice(1).join('^') + '\n'; lineNumber++
                })
            })
        }

        // Section 194P Records (94P) - For 24Q Q4 only
        let seniorEntriesToReport = seniorCitizenData || [];
        if (selectedReturnType === 'Correction') {
            seniorEntriesToReport = seniorEntriesToReport.filter(sl => {
                const original = originalSeniorCitizenData.find(o => o.seniorCitizenPan === sl.seniorCitizenPan)
                if (!original) return true; // New record

                const hasChanged =
                    parseFloat(sl.grossPension17_1ii || 0) !== parseFloat(original.grossPension17_1ii || 0) ||
                    parseFloat(sl.salariesIncome || 0) !== parseFloat(original.salariesIncome || 0) ||
                    parseFloat(sl.interestIncome || 0) !== parseFloat(original.interestIncome || 0) ||
                    parseFloat(sl.totalTaxableIncome || 0) !== parseFloat(original.totalTaxableIncome || 0) ||
                    parseFloat(sl.netTaxPayable || 0) !== parseFloat(original.netTaxPayable || 0) ||
                    sl.seniorCitizenName !== original.seniorCitizenName;

                return hasChanged;
            })
        }

        if (seniorEntriesToReport.length > 0) {
            seniorEntriesToReport.forEach((slItem, slIndex) => {
                const sl = []

                // Identify non-zero Section 16 and Chapter VI-A for 94P
                const s16Details = []
                if (parseFloat(slItem.ded16ia || 0) > 0) s16Details.push({ id: '16(ia)', amount: slItem.ded16ia })
                if (parseFloat(slItem.ded16iii || 0) > 0) s16Details.push({ id: '16(iii)', amount: slItem.ded16iii })

                const c6aDetails = []
                const sections = [
                    { id: '80C', key: 'sec80C' },
                    { id: '80CCC', key: 'sec80CCC' },
                    { id: '80CCD(1)', key: 'sec80CCD1' },
                    { id: '80CCD(1B)', key: 'sec80CCD1B' },
                    { id: '80D', key: 'sec80D' },
                    { id: '80E', key: 'sec80E' },
                    { id: '80G', key: 'sec80G' },
                    { id: '80TTB', key: 'sec80TTB' },
                    { id: '80DD', key: 'sec80DD' },
                    { id: '80EE', key: 'sec80EE' },
                    { id: '80EEA', key: 'sec80EEA' },
                    { id: '80GG', key: 'sec80GG' },
                    { id: '80GGC', key: 'sec80GGC' },
                    { id: '80U', key: 'sec80U' },
                    { id: 'OTHERS', key: 'secOthers' }
                ]

                sections.forEach(sec => {
                    const ded = parseFloat(slItem[sec.key] || 0)
                    if (ded > 0) {
                        const hasQualifying = ['sec80G', 'sec80TTB', 'sec80DD', 'sec80EE', 'sec80EEA', 'sec80GG', 'sec80GGC', 'sec80U', 'secOthers'].includes(sec.key)
                        c6aDetails.push({
                            id: sec.id,
                            deductible: ded,
                            gross: parseFloat(slItem[`${sec.key}_Gross`] || 0),
                            // Qualifying = empty for sections with no qualifying limit (80C, 80CCC, 80CCD1, 80CCD1B, 80D, 80E);
                            // only populated from user input for 80G, 80TTB, 80DD, 80EE, 80EEA, 80GG, 80GGC, 80U, OTHERS
                            qualifying: hasQualifying
                                ? parseFloat(slItem[`${sec.key}_Qual`] || 0)
                                : null
                        })
                    }
                })

                sl[1] = lineNumber // 1. Line Number
                sl[2] = '94P' // 2. Record Type
                sl[3] = '1' // 3. Batch Number
                sl[4] = slIndex + 1 // 4. Section 194P Details Record No
                sl[5] = 'A' // 5. Mode
                sl[6] = slItem.seniorCitizenPan || '' // 6. Specified Senior Citizen PAN
                sl[7] = slItem.seniorCitizenName || '' // 7. Name of Specified Senior Citizen
                sl[8] = slItem.category || 'S' // 8. Category (Senior/Super Senior)
                sl[9] = slItem.regime115BAC || 'N' // 9. Opting for 115BAC (Y/N)
                sl[10] = formatDecimalAmount(slItem.grossPension17_1ii) // 10. Gross Pension
                sl[11] = s16Details.length // 11. Count of Section 194P - Section 16 Detail Records
                sl[12] = formatDecimalAmount(slItem.totalDed16) // 12. Gross Total of Deduction u/s 16
                sl[13] = formatDecimalAmount(slItem.salariesIncome) // 13. Income under "Salaries"
                sl[14] = formatDecimalAmount(slItem.interestIncome) // 14. Interest Income (Other Sources)
                sl[15] = formatDecimalAmount(slItem.grossTotalIncome) // 15. Gross Total Income
                sl[16] = formatDecimalAmount(slItem.lastGrossTotalIncome) // 16. Last Gross Total Income
                sl[17] = formatDecimalAmount(slItem.ded80C_CCC_CCD1) // 17. Total Deduction u/s 80C, 80CCC, 80CCD(1)
                sl[18] = c6aDetails.length // 18. Count of Section 194P - Chapter VI-A Details
                sl[19] = formatDecimalAmount(slItem.aggChapterVIA) // 19. Aggregate of deductible amount under Chapter VI-A
                sl[20] = formatDecimalAmount(slItem.totalTaxableIncome) // 20. Total Taxable Income
                sl[21] = formatDecimalAmount(slItem.incomeTax) // 21. Tax on total income
                sl[22] = formatDecimalAmount(slItem.rebate87A) // 22. Rebate u/s 87A
                sl[23] = formatDecimalAmount(slItem.surcharge) // 23. Surcharge
                sl[24] = formatDecimalAmount(slItem.cess) // 24. Health and education cess
                sl[25] = formatDecimalAmount(slItem.taxPayable) // 25. Tax Payable
                sl[26] = formatDecimalAmount(slItem.relief89) // 26. Relief u/s 89
                sl[27] = formatDecimalAmount(slItem.netTaxPayable) // 27. Net tax Payable
                sl[28] = '' // 28. Record Hash

                fileContent += sl.slice(1).join('^') + '\n'; lineNumber++

                // Added P16 records for 94P
                s16Details.forEach((sec16, sIdx) => {
                    const p16 = []
                    p16[1] = lineNumber
                    p16[2] = 'P16'
                    p16[3] = '1'
                    p16[4] = slIndex + 1
                    p16[5] = sIdx + 1
                    p16[6] = sec16.id
                    p16[7] = formatDecimalAmount(sec16.amount)
                    p16[8] = ''

                    fileContent += p16.slice(1).join('^') + '\n'; lineNumber++
                })

                // Added P6A records for 94P
                c6aDetails.forEach((c6a, cIdx) => {
                    const p6a = []
                    p6a[1] = lineNumber
                    p6a[2] = 'P6A'
                    p6a[3] = '1'
                    p6a[4] = slIndex + 1
                    p6a[5] = cIdx + 1
                    p6a[6] = c6a.id
                    p6a[7] = formatDecimalAmount(c6a.gross)
                    p6a[8] = c6a.qualifying !== null ? formatDecimalAmount(c6a.qualifying) : '' // 8. Qualifying amount (empty if no qualifying limit for this section)
                    p6a[9] = formatDecimalAmount(c6a.deductible)
                    p6a[10] = ''

                    fileContent += p6a.slice(1).join('^') + '\n'; lineNumber++
                })
            })
        }

        return fileContent
    }





    const handleCsiFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0]
            setCsiFile(selectedFile)
            setShowCsiUploadPopup(false) // Close popup after file is chosen
            // Pass the pre-built file content (generated at FVU trigger time) if available
            executeValidation(selectedFile, pendingFileContentRef.current)
        }
        // Reset input value so the same file can be re-selected on next attempt
        if (csiFileInputRef.current) {
            csiFileInputRef.current.value = ''
        }
    }



    return (
        <div className="min-h-screen bg-gray-50 w-full overflow-x-hidden">
            <ToastContainer position="top-right" autoClose={3000} />
            <TdsSidebar
                isOpen={sidebarOpen}
                isMobile={isMobile}
                onClose={() => setSidebarOpen(false)}
                activeSection={activeSection}
                onSectionChange={setActiveSection}
                onSidebarStateChange={handleSidebarStateChange}
                profileData={profileData}
                tdsData={tdsData}
                challanData={challanData}
                showSalaryDetails={true}
                show194P={true}
            />

            {/* Header */}
            <header className={`bg-white shadow-sm border-b transition-all duration-300 ${!isMobile ? (sidebarState.isExpanded ? 'ml-64' : 'ml-14') : ''}`} style={{
                width: !isMobile ? (sidebarState.isExpanded ? 'calc(100% - 16rem)' : 'calc(100% - 3.5rem)') : '100%'
            }}>
                <div className="w-full px-2 sm:px-4 lg:px-6">
                    <div className="flex justify-between items-center h-16 relative">
                        <div className="flex items-center">
                        </div>
                        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                            <h1 className="text-xl font-bold text-gray-800">Form 24Q (Salary) - Quarter 4</h1>
                        </div>
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={handleClearData}
                                className="flex items-center text-red-600 hover:text-red-700 px-3 py-2 rounded-md text-sm font-medium hover:bg-red-50 transition-colors"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Clear Data
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className={`py-4 px-2 sm:px-4 lg:px-6 transition-all duration-300 ${!isMobile ? (sidebarState.isExpanded ? 'ml-64' : 'ml-14') : ''}`} style={{
                width: !isMobile ? (sidebarState.isExpanded ? 'calc(100% - 16rem)' : 'calc(100% - 3.5rem)') : '100%',
                maxWidth: '100%',
                overflowX: 'hidden',
                minHeight: 'calc(100vh - 4rem)'
            }}>

                {/* Financial Year and Form Selection */}
                <div className="mb-8 bg-white rounded-lg shadow-md p-6">
                    <div className="flex flex-col lg:flex-row w-full gap-4 items-end">
                        <div className="flex-1 min-w-[180px] w-full">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Financial Year (FY) <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={selectedFinancialYear}
                                onChange={(e) => {
                                    const newYear = e.target.value;
                                    setSelectedFinancialYear(newYear);
                                    localStorage.setItem('tdsFinancialYear', newYear);

                                    // Auto-update selectedForm if it doesn't match the year's format
                                    if (newYear) {
                                        const isNewFormat = parseInt(newYear.split('-')[0]) >= 2026;
                                        setSelectedForm(prevForm => {
                                            const f = prevForm;
                                            const mapped = isNewFormat
                                                ? (f === '24Q' ? '138' : (f === '26Q' || f === '140') ? '140' : f === '27Q' ? '144' : f === '27EQ' ? '143' : f === '24Q4' ? '24Q4' : f)
                                                : (f === '138' ? '24Q' : (f === '140' || f === '26Q') ? '26Q' : f === '144' ? '27Q' : f === '143' ? '27EQ' : f === '24Q4' ? '24Q4' : f);
                                            if (mapped !== prevForm) {
                                                localStorage.setItem('tdsForm', mapped);
                                                return mapped;
                                            }
                                            return prevForm;
                                        });
                                    }
                                }}
                                className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
                            >
                                <option value="">Select Financial Year</option>
                                {financialYearOptions.map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex-1 min-w-[180px] w-full">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Quarter <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={selectedQuarter}
                                onChange={(e) => {
                                    setSelectedQuarter(e.target.value)
                                    localStorage.setItem('tdsQuarter', e.target.value)
                                }}
                                className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
                            >
                                <option value="">Select Quarter</option>
                                {quarterOptions.map(q => <option key={q} value={q}>{q}</option>)}
                            </select>
                        </div>

                        <div className="flex-1 min-w-[180px] w-full">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Form Type <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={selectedForm}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setSelectedForm(val)
                                    localStorage.setItem('tdsForm', val)
                                }}
                                className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
                            >
                                {formOptions.map(formVal => {
                                    const f = formVal;
                                    const actualForm = selectedFinancialYear && parseInt(selectedFinancialYear.split('-')[0]) >= 2026
                                        ? (f === '24Q' ? '138' : (f === '26Q' || f === '140') ? '140' : f === '27Q' ? '144' : f === '27EQ' ? '143' : f)
                                        : (f === '138' ? '24Q' : (f === '140' || f === '26Q') ? '26Q' : f === '144' ? '27Q' : f === '143' ? '27EQ' : f);

                                    return (
                                        <option key={formVal} value={actualForm}>
                                            {actualForm}
                                        </option>
                                    )
                                })}
                            </select>
                        </div>

                        <div className="flex-1 min-w-[180px] w-full">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Return Type <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={selectedReturnType}
                                onChange={(e) => {
                                    setSelectedReturnType(e.target.value)
                                    localStorage.setItem('SELECTED_RETURN_TYPE', e.target.value)
                                }}
                                className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
                            >
                                <option value="Regular">Regular</option>
                                <option value="Correction">Correction</option>
                            </select>
                        </div>

                        <div className="flex-1 min-w-[200px] w-full flex items-center mb-1">
                            <button
                                onClick={() => {
                                    const targetForm = selectedForm ? selectedForm.toLowerCase() : '24q';
                                    const isCorrection = selectedReturnType === 'Correction';
                                    const suffix = isCorrection ? '-correction' : '';
                                    const targetPath = `/tds-dashboard/${targetForm}${suffix}`;

                                    if (targetForm !== '24q') {
                                        navigate(targetPath);
                                    } else if (selectedQuarter && selectedQuarter !== 'Q4') {
                                        navigate(targetPath);
                                    } else if (location.pathname !== targetPath) {
                                        navigate(targetPath);
                                    }
                                    toast.success('Selection Applied');
                                }}
                                className="w-full px-8 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 transition-all font-semibold shadow-lg hover:shadow-orange-200"
                            >
                                Apply Selection
                            </button>
                        </div>
                    </div>
                </div>

                {/* Receipt Details Section (Conditional) */}
                {(selectedReturnType === 'Correction' || selectedReturnType === 'Regular') && (
                    <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100 overflow-hidden relative">
                        <div className="absolute top-0 left-0 w-2 h-full bg-orange-500"></div>
                        <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
                            <Building2 className="w-5 h-5 mr-2 text-orange-600" />
                            Statement Receipt Details <span className="text-red-500 ml-1">*</span>
                        </h3>

                        <div className="space-y-6">
                            {/* REVISED RETURN CASE - PREMIUM UI */}
                            {selectedReturnType === 'Correction' && (
                                <div className="bg-orange-50 rounded-xl p-6 border border-orange-100">
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                                        {/* Conso Upload Area */}
                                        <div className="lg:col-span-1">
                                            <div className="flex flex-col items-center">
                                                <input
                                                    type="file"
                                                    accept=".tds"
                                                    onChange={handleConsoFileUpload}
                                                    className="hidden"
                                                    id="conso-upload-premium"
                                                />
                                                <label
                                                    htmlFor="conso-upload-premium"
                                                    className="group relative flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-orange-300 rounded-2xl bg-white hover:bg-orange-50 hover:border-orange-500 transition-all cursor-pointer overflow-hidden shadow-sm"
                                                >
                                                    {consoFileContent ? (
                                                        <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="flex flex-col items-center text-green-600 text-center">
                                                            <div className="bg-green-100 p-3 rounded-full mb-2">
                                                                <FileText className="w-8 h-8" />
                                                            </div>
                                                            <span className="text-sm font-bold">Conso File Ready</span>
                                                            <span className="text-xs text-green-500 mt-1">Click to Replace</span>
                                                        </motion.div>
                                                    ) : (
                                                        <div className="flex flex-col items-center text-gray-500 text-center">
                                                            <div className="bg-orange-100 p-3 rounded-full mb-3 group-hover:bg-orange-200 transition-colors">
                                                                <Upload className="w-8 h-8 text-orange-600" />
                                                            </div>
                                                            <span className="text-sm font-bold text-gray-700 group-hover:text-orange-700">Upload Conso File</span>
                                                            <span className="text-[10px] text-gray-400 mt-1 uppercase tracking-wider font-medium">Click to browse (.tds)</span>
                                                        </div>
                                                    )}

                                                    {/* Animated background glow */}
                                                    <div className="absolute inset-0 bg-blue-400 opacity-0 group-hover:opacity-5 transition-opacity pointer-events-none"></div>
                                                </label>

                                                <p className="mt-3 text-xs text-gray-500 text-center flex items-center">
                                                    <User className="w-3 h-3 mr-1" />
                                                    Upload Consolidated .tds file from TRACES
                                                </p>
                                            </div>
                                        </div>

                                        {/* Auto-filled Fields */}
                                        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <label className="text-xs font-bold text-orange-700 uppercase tracking-wider">
                                                    Receipt No. of Original Return
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        value={profileData?.section1?.receiptNoOriginal || ''}
                                                        disabled
                                                        className="w-full px-4 py-3 bg-white border border-orange-200 rounded-xl text-gray-700 font-medium shadow-sm cursor-not-allowed"
                                                        placeholder="Waiting for Conso upload..."
                                                    />
                                                    {profileData?.section1?.receiptNoOriginal && (
                                                        <div className="absolute right-3 top-1/2 -translate-y-1/2 bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded-full font-bold">
                                                            Verified
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-xs font-bold text-orange-700 uppercase tracking-wider">
                                                    Receipt No. of Earlier Statement
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        value={profileData?.section1?.receiptNoEarlier || ''}
                                                        disabled
                                                        className="w-full px-4 py-3 bg-white border border-orange-200 rounded-xl text-gray-700 font-medium shadow-sm cursor-not-allowed"
                                                        placeholder="Waiting for Conso upload..."
                                                    />
                                                    {profileData?.section1?.receiptNoEarlier && (
                                                        <div className="absolute right-3 top-1/2 -translate-y-1/2 bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded-full font-bold">
                                                            Verified
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="md:col-span-2 flex items-center p-3 bg-white rounded-lg border border-orange-100 shadow-sm">
                                                <div className="bg-orange-100 p-2 rounded-lg mr-3">
                                                    <ArrowLeft className="w-4 h-4 text-orange-600" />
                                                </div>
                                                <p className="text-xs text-gray-600">
                                                    <span className="font-bold text-orange-600">Note:</span> These details are auto-extracted from your Conso file to ensure correctness and prevent validation failures.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {selectedReturnType === 'Regular' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Is earlier Return Filed?
                                        </label>
                                        <select
                                            value={profileData?.section1?.isEarlierReturnFiled || 'No'}
                                            onChange={(e) => handleProfileInputChange('section1.isEarlierReturnFiled', e.target.value)}
                                            className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                                        >
                                            <option value="No">No</option>
                                            <option value="Yes">Yes</option>
                                        </select>
                                    </div>
                                    {profileData?.section1?.isEarlierReturnFiled === 'Yes' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Receipt No. of Original Return <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={profileData?.section1?.receiptNoOriginal || ''}
                                                onChange={(e) => handleProfileInputChange('section1.receiptNoOriginal', e.target.value)}
                                                required
                                                className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
                                                placeholder="Enter 15-digit Receipt Number"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Section Content */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    {activeSection === 'tds-details' && (
                        <TdsDetailsTable
                            tdsData={tdsData}
                            challanData={challanData}
                            onTdsDataChange={handleTdsDataChange}
                            sectionOptions={selectedFinancialYear && parseInt(selectedFinancialYear.split('-')[0]) >= 2026 ? sectionOptions24Q_new : sectionOptions24Q_old}
                            is24Q4={true}
                            hideRateColumn={true}
                            isRevised={selectedReturnType === 'Correction'}
                            originalData={originalTdsData}
                        />
                    ) || activeSection === 'challan-details' && (

                        <ChallanDetailsTable
                            challanData={challanData}
                            tdsData={tdsData}
                            onChallanDataChange={handleChallanDataChange}
                            tan={profileData?.section1?.taxDeduction}
                            financialYear={selectedFinancialYear}
                            isRevised={selectedReturnType === 'Correction'}
                            sectionOptions={selectedFinancialYear && parseInt(selectedFinancialYear.split('-')[0]) >= 2026 ? sectionOptions24Q_new : sectionOptions24Q_old}
                        />
                    ) || activeSection === 'salary-details' && (
                        <SalaryDetailsTable
                            salaryData={salaryData}
                            onSalaryDataChange={handleSalaryDataChange}
                            isRevised={selectedReturnType === 'Correction'}
                            originalSalaryData={originalSalaryData}
                        />
                    ) || activeSection === 'senior-citizens-194p' && (
                        <SeniorCitizens194PTable
                            seniorCitizenData={seniorCitizenData}
                            onSeniorCitizenDataChange={handleSeniorCitizenDataChange}
                            isRevised={selectedReturnType === 'Correction'}
                            originalData={originalSeniorCitizenData}
                        />
                    ) || activeSection === 'profile' && (
                        <TdsProfileSection
                            profileData={profileData}
                            isProfileEditing={isProfileEditing}
                            onProfileDataChange={handleProfileInputChange}
                            onPrefillClick={() => setShowPrefillPrompt(true)}
                        />
                    )}
                </div>


            </main>

            {/* CSI confirmation and other modals */}
            {/* ... Modal code remains below ... */}

            {showPrefillPrompt && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
                        <h3 className="text-xl font-bold mb-4">Pre-fill Profile Data</h3>
                        <div className="relative mb-4">
                            <input
                                type={showPrefillPassword ? "text" : "password"}
                                className="w-full border rounded p-2 pr-10"
                                value={prefillPassword}
                                onChange={(e) => setPrefillPassword(e.target.value)}
                                placeholder="Portal Password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPrefillPassword(!showPrefillPassword)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                            >
                                {showPrefillPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                        <div className="flex space-x-3">
                            <button
                                onClick={handleProfilePrefill}
                                disabled={isExtractingProfile}
                                className={`flex-1 py-2 rounded font-bold transition-colors ${isExtractingProfile ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md'}`}
                            >
                                {isExtractingProfile ? 'Extracting...' : 'Get Data'}
                            </button>
                            <button onClick={() => setShowPrefillPrompt(false)} className="flex-1 bg-gray-200 py-2 rounded font-medium hover:bg-gray-300 transition-colors">Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {showCsiUploadPopup && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50">
                    <div className="bg-white p-6 rounded-lg">
                        <h3 className="text-xl font-bold mb-4">Validate TDS File</h3>
                        <button onClick={() => setShowDownloadPrompt(true)} className="w-full bg-blue-600 text-white py-2 mb-2 rounded">Download CSI</button>
                        <button onClick={() => csiFileInputRef.current.click()} className="w-full bg-green-600 text-white py-2 rounded">Upload CSI</button>
                        <button onClick={() => setShowCsiUploadPopup(false)} className="w-full mt-4 text-gray-500">Close</button>
                    </div>
                </div>
            )}

            {showDownloadPrompt && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md transform transition-all scale-100 relative">
                        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                            <Upload className="w-5 h-5 mr-2 text-blue-600" />
                            Download CSI File
                        </h3>

                        <p className="text-gray-600 mb-4 text-sm">
                            Would you like to automatically download the latest CSI file from the Income Tax Portal?
                        </p>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Income Tax Password</label>
                            <div className="relative">
                                <input
                                    type={showDownloadPassword ? "text" : "password"}
                                    className="w-full border border-gray-300 rounded-md p-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    value={downloadPassword}
                                    onChange={(e) => setDownloadPassword(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleCsiDownload()}
                                    placeholder="Enter Password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowDownloadPassword(!showDownloadPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                                >
                                    {showDownloadPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">TAN: {profileData.section1?.taxDeduction}</p>
                        </div>

                        <div className="flex space-x-3">
                            <button
                                onClick={handleCsiDownload}
                                disabled={isDownloadingCsi}
                                className={`flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition flex items-center justify-center border border-transparent shadow-md ${isDownloadingCsi ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                {isDownloadingCsi ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                        Downloading...
                                    </>
                                ) : (
                                    <span className="font-medium">Download &amp; Continue</span>
                                )}
                            </button>
                            <button
                                onClick={handleSkipDownload}
                                disabled={isDownloadingCsi}
                                className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition font-medium"
                            >
                                Skip (I have file)
                            </button>
                        </div>

                        <button
                            onClick={() => setShowDownloadPrompt(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            )}

            <input type="file" ref={csiFileInputRef} style={{ display: 'none' }} onChange={handleCsiFileChange} />

            {/* Validation Lock Screen Overlay */}
            {isValidating && (
                <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gray-900 bg-opacity-80 backdrop-blur-sm cursor-wait">
                    <div className="bg-white p-8 rounded-2xl shadow-2xl flex flex-col items-center max-w-lg text-center">
                        <div className="relative mb-6">
                            <div className="absolute inset-0 bg-orange-100 rounded-full animate-ping opacity-75"></div>
                            <div className="relative bg-white rounded-full p-2">
                                <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-200 border-t-orange-600"></div>
                            </div>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">Validation in Progress</h3>
                        <p className="text-gray-600 mb-6">Please wait while we validate your return.</p>
                        <button
                            onClick={handleCancelValidation}
                            className="px-6 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium transition-colors border border-red-200"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>

    )
}

export default Tds24Q4Form
